export interface SearchResultAnnouncementSources {
  query: string;
  totalProductsCount: string;
  noResultsMessage: string | null;
  resultsMessageTemplate: string;
}

const ANNOUNCER_SELECTOR = '[data-container="search-result-announcer"]';

// VoiceOver echoes each typed word and that echo cancels anything queued in a polite live region,
// so the result message is only written once typing has settled. Additive to the 500ms debounce on
// the search input itself (search.ts), which already delays the request behind these keystrokes.
const TYPING_SETTLE_DELAY_MS = 700;

// Screen readers ignore a live region update that writes the same text again, so the region is
// emptied first and refilled on a later task to force a fresh announcement.
const REPEAT_ANNOUNCEMENT_DELAY_MS = 100;

let pendingAnnouncement: ReturnType<typeof setTimeout> | null = null;

const cancelPendingAnnouncement = () => {
  if (pendingAnnouncement !== null) clearTimeout(pendingAnnouncement);
  pendingAnnouncement = null;
};

export const getSearchResultAnnouncement = (sources: SearchResultAnnouncementSources): string => {
  if (sources.query === '') return '';

  if (sources.noResultsMessage) return sources.noResultsMessage;

  if (sources.totalProductsCount !== '' && sources.totalProductsCount !== '0' && sources.resultsMessageTemplate !== '') {
    return `${sources.totalProductsCount} ${sources.resultsMessageTemplate}`;
  }

  return '';
};

export const clearSearchResultAnnouncement = (searchBar: HTMLElement | null): void => {
  cancelPendingAnnouncement();

  const announcer = searchBar?.querySelector<HTMLElement>(ANNOUNCER_SELECTOR);
  if (announcer) announcer.textContent = '';
};

export const announceSearchResult = (searchBar: HTMLElement | null, query: string, searchResults: HTMLElement | null): void => {
  const announcer = searchBar?.querySelector<HTMLElement>(ANNOUNCER_SELECTOR);
  if (!announcer) return;

  const totalProductsCount = searchResults?.querySelector('[data-container="total-products-count"]')?.textContent?.trim() ?? '';
  const noResultContainer = searchResults?.querySelector('[data-no-results="true"]');

  const message = getSearchResultAnnouncement({
    query,
    totalProductsCount,
    noResultsMessage: noResultContainer?.getAttribute('data-aria-label') ?? null,
    resultsMessageTemplate: announcer.dataset.resultsTemplate ?? '',
  });

  if (message === '') {
    clearSearchResultAnnouncement(searchBar);
    return;
  }

  cancelPendingAnnouncement();

  const isRepeat = announcer.textContent === message;

  pendingAnnouncement = setTimeout(() => {
    if (!isRepeat) {
      pendingAnnouncement = null;
      announcer.textContent = message;
      return;
    }

    announcer.textContent = '';
    pendingAnnouncement = setTimeout(() => {
      pendingAnnouncement = null;
      announcer.textContent = message;
    }, REPEAT_ANNOUNCEMENT_DELAY_MS);
  }, TYPING_SETTLE_DELAY_MS);
};
